// Small, deterministic teaching models. These are not PolyCFD's 3-D solver.
export const TAU = 2 * Math.PI;
export const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));
export const periodic = (x: number, n: number) => ((x % n) + n) % n;
export const rms = (a: number[]) => Math.sqrt(a.reduce((s, v) => s + v * v, 0) / a.length);
export function derivative(x: number, h: number) {
  const approximate = (Math.sin(x + h) - Math.sin(x - h)) / (2 * h);
  return { approximate, exact: Math.cos(x), error: Math.abs(approximate - Math.cos(x)) };
}
export function gaussian(x: number) {
  const d = periodic(x - .25 + .5, 1) - .5;
  return Math.exp(-d * d / (.075 * .075));
}
export function transport(n: number, cfl: number, steps: number, scheme: 'sl' | 'mc') {
  let values = Array.from({ length: n }, (_, i) => gaussian(i / n));
  const sample = (a: number[], x: number) => {
    const i = Math.floor(x), w = x - i;
    return (1 - w) * a[periodic(i, n)] + w * a[periodic(i + 1, n)];
  };
  for (let k = 0; k < steps; k++) {
    const forward = values.map((_, i) => sample(values, i - cfl));
    if (scheme === 'mc') {
      const reverse = forward.map((_, i) => sample(forward, i + cfl));
      values = values.map((u, i) => {
        const j = Math.floor(i - cfl), a = values[periodic(j, n)], b = values[periodic(j + 1, n)];
        return clamp(forward[i] + .5 * (u - reverse[i]), Math.min(a, b), Math.max(a, b));
      });
    } else values = forward;
  }
  const exact = values.map((_, i) => gaussian(i / n - steps * cfl / n));
  return { values, exact, error: rms(values.map((v, i) => v - exact[i])), peak: Math.max(...values), time: steps * cfl / n };
}
export function divergence(u: number[], v: number[], n: number) {
  const idx = (i: number, j: number) => periodic(i, n) + n * periodic(j, n);
  return u.map((_, k) => { const i = k % n, j = Math.floor(k / n); return n * (u[idx(i + 1, j)] - u[k] + v[idx(i, j + 1)] - v[k]); });
}
export function project(n = 8, dt = .1) {
  const idx = (i: number, j: number) => periodic(i, n) + n * periodic(j, n);
  const u = Array.from({ length: n * n }, (_, k) => Math.sin(TAU * (k % n) / n) + .4 * Math.sin(TAU * (Math.floor(k / n) + .5) / n));
  const v = Array.from({ length: n * n }, (_, k) => .6 * Math.sin(TAU * Math.floor(k / n) / n) - .4 * Math.sin(TAU * (k % n + .5) / n));
  const before = divergence(u, v, n), b = before.map(x => -x / dt);
  let pressure = u.map(() => 0);
  for (let iteration = 0; iteration < 500; iteration++) {
    pressure = pressure.map((p, k) => {
      const i = k % n, j = Math.floor(k / n);
      const jacobi = (pressure[idx(i - 1, j)] + pressure[idx(i + 1, j)] + pressure[idx(i, j - 1)] + pressure[idx(i, j + 1)] + b[k] / (n * n)) / 4;
      return .2 * p + .8 * jacobi;
    });
  }
  const correctedU = u.map((x, k) => x - dt * n * (pressure[k] - pressure[idx(k % n - 1, Math.floor(k / n))]));
  const correctedV = v.map((x, k) => x - dt * n * (pressure[k] - pressure[idx(k % n, Math.floor(k / n) - 1)]));
  return { u, v, correctedU, correctedV, before, after: divergence(correctedU, correctedV, n), pressure };
}
export function smoothError(iterations: number, mode: 'low' | 'high' | 'mixed', n = 32) {
  const initial = Array.from({ length: n + 1 }, (_, i) => (mode === 'high' ? 0 : Math.sin(Math.PI * i / n)) + (mode === 'low' ? 0 : .5 * Math.sin(15 * Math.PI * i / n)));
  initial[0] = 0; initial[n] = 0;
  let error = [...initial]; const history = [rms(error)];
  for (let k = 0; k < iterations; k++) {
    error = error.map((v, i) => i === 0 || i === n ? 0 : v / 3 + (error[i - 1] + error[i + 1]) / 3);
    history.push(rms(error));
  }
  return { initial, error, history };
}
export function midpointFlow(n: number) {
  return Array.from({ length: n }, (_, i) => { const x = (i + .5) / n; return 4 * x * (1 - x) / n; }).reduce((a, b) => a + b, 0);
}
